import { useState } from 'react';
import { useInvalidate, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import {
  Table,
  Input,
  Tag,
  Form,
  Row,
  Col,
  Flex,
  Typography,
  Button,
  theme,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  ContentMasterAssetFileOutAvailability,
  ProductBulkAction,
  ProductFormat,
  ProductType,
  TranscodingJobStatus,
} from '@/api/generated/schemas';
import type { ContentMasterAssetFileOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useDragRowSelection } from '@/hooks/useDragRowSelection';
import { ProductBulkActions } from '@/components/content_catalog/products/ProductBulkActions';
import { MasterAssetFileAlerts } from '@/components/content_catalog/master_asset_files/MasterAssetFileAlerts';
import { MasterAssetFileViewSelect } from '@/components/content_catalog/master_asset_files/MasterAssetFileViewSelect';
import {
  masterFileView,
  viewOwnsField,
} from '@/components/content_catalog/master_asset_files/viewQueries';
import type { MasterFileViewKey } from '@/components/content_catalog/master_asset_files/viewQueries';
import { PublisherScopedListTitle } from '@/components/content_catalog/PublisherScopedListTitle';
import { usePublisherScope } from '@/hooks/usePublisherScope';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { BooleanFilter } from '@/components/common/BooleanFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { PresencePill } from '@/components/common/PresencePill';
import { SelectFilter } from '@/components/common/SelectFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { TextFilter } from '@/components/common/TextFilter';
import {
  availabilityColors,
  productTypeColors,
  transcodingStatusColors,
} from '@/pages/content_catalog/products/constants';
import { isFieldFiltered } from '@/utils/filters';
import { formatFileSizeComma } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const MasterAssetFileList = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const f = (key: string) =>
    t(`content_catalog.master_asset_files.fields.${key}`);

  const { tableProps, searchFormProps, sorters, filters, setFilters } =
    useAdminTable<ContentMasterAssetFileOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.master_asset_files',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const crudFilters = filters as CrudFilter[];
  const invalidate = useInvalidate();

  // Rows are files, but the bulk actions act on products: several selected
  // files of one product collapse into a single product id.
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const rows = (tableProps.dataSource ?? []) as ContentMasterAssetFileOut[];
  // Selection is of visible rows: an id left behind by a page or filter
  // change would inflate the count while the action acted on fewer products.
  const visibleSelectedIds = selectedIds.filter((id) =>
    rows.some((r) => r.id === id)
  );
  const selectedRows = rows.filter((r) => visibleSelectedIds.includes(r.id));
  const productIds = [...new Set(selectedRows.map((r) => r.product_id))];
  const titleById = Object.fromEntries(
    selectedRows
      .filter((r) => r.product_title)
      .map((r) => [r.product_id, r.product_title as string])
  ) as Record<number, string>;
  // Press a checkbox and drag to select (or deselect) a run of rows.
  const drag = useDragRowSelection<number>({
    keys: rows.map((r) => r.id),
    selected: visibleSelectedIds,
    onChange: setSelectedIds,
  });
  // A bulk run outlives the selection it started from — see the bulk bar.
  const [bulkOpen, setBulkOpen] = useState(false);
  const total =
    typeof tableProps.pagination === 'object'
      ? tableProps.pagination.total
      : undefined;

  const { publisherId, setPublisherScope } = usePublisherScope(
    crudFilters,
    setFilters,
    () => setSelectedIds([])
  );

  const scope: CrudFilter[] =
    publisherId != null
      ? [{ field: 'publisher_id', operator: 'eq', value: publisherId }]
      : [];

  // The view is write-only state: picking one writes its filters, and nothing
  // reads a view back out of them. A link that already carries filters
  // therefore opens with no view named — the filters still apply.
  const [activeView, setActiveView] = useState<MasterFileViewKey | null>(null);
  const applyView = (key: MasterFileViewKey | null) => {
    setActiveView(key);
    setSelectedIds([]);
    searchFormProps.form?.setFieldsValue({ q: '' });
    setFilters([...scope, ...(masterFileView(key)?.filters ?? [])], 'replace');
  };
  /**
   * Removing one of a view's own filters means the rows are no longer that
   * view, so it stops being named — column filters leave the name alone.
   */
  const dropViewLabelIfPartOfIt = (field: string) => {
    if (viewOwnsField(activeView, field)) setActiveView(null);
  };

  const formatFilterValue = (
    field: string,
    value: string | number | boolean
  ) => {
    if (typeof value === 'boolean')
      return t(value ? 'common.yes' : 'common.no');
    if (field === 'product_type')
      return t(`content_catalog.products.product_type.${value}`);
    if (field === 'transcoding_status')
      return t(`content_catalog.transcoding_jobs.status.${value}`);
    if (field === 'availability')
      return t(`content_catalog.distribution_periods.filters.${value}`);
    return undefined;
  };

  const booleanColumn = (
    field: 'is_latest' | 'is_downloaded' | 'publisher_has_active_contract'
  ) => (
    <Table.Column<ContentMasterAssetFileOut>
      key={field}
      dataIndex={field}
      title={f(field)}
      sorter
      sortOrder={getSortOrder(sorters, field)}
      render={(value: boolean | undefined) => (
        <PresencePill present={!!value} label={f(field)} />
      )}
      filtered={isFieldFiltered(crudFilters, field)}
      filterDropdown={({ confirm, clearFilters }) => (
        <BooleanFilter
          field={field}
          placeholder={f(field)}
          filters={crudFilters}
          confirm={confirm}
          clearFilters={clearFilters}
          setFilters={setFilters}
        />
      )}
    />
  );

  return (
    <List
      canCreate={false}
      title={
        <PublisherScopedListTitle
          title={t('content_catalog.master_asset_files.titles.list')}
          total={total}
          totalKey="content_catalog.master_asset_files.context.total"
          publisherId={publisherId}
          onPublisherChange={setPublisherScope}
        />
      }
    >
      <MasterAssetFileAlerts
        activeKey={activeView}
        scope={scope}
        onSelect={(query) => applyView(query?.key ?? null)}
      />

      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <MasterAssetFileViewSelect
              value={activeView}
              onChange={applyView}
            />
          </Col>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'content_catalog.master_asset_files.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {activeView && (
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          {t(
            `content_catalog.master_asset_files.view_descriptions.${activeView}`
          )}
        </Typography.Paragraph>
      )}

      <ActiveFilters
        filters={crudFilters}
        setFilters={setFilters}
        labelPrefix="content_catalog.master_asset_files"
        // The switcher and the search box speak for these two.
        ignore={['publisher_id', 'q']}
        formatValue={formatFilterValue}
        onRemove={dropViewLabelIfPartOfIt}
      />

      {/* The bar also stays while the modal is open: the refetch after a run
          can drop acted-on rows out of the view, and unmounting here would
          take the result view with it. */}
      {(visibleSelectedIds.length > 0 || bulkOpen) && (
        <Flex
          align="center"
          gap={token.marginSM}
          style={{ marginBottom: token.marginSM }}
        >
          {visibleSelectedIds.length > 0 && (
            <Typography.Text>
              {t('content_catalog.master_asset_files.bulk.selected', {
                files: visibleSelectedIds.length,
                products: productIds.length,
              })}
            </Typography.Text>
          )}
          <ProductBulkActions
            ids={productIds}
            // Cover publishing is off-topic for a list of master files.
            actions={[
              ProductBulkAction.reconcile_asset_downloads,
              ProductBulkAction.reconcile_transcoding,
            ]}
            titles={titleById}
            onDone={() =>
              invalidate({
                resource: 'content_catalog.master_asset_files',
                invalidates: ['list'],
              })
            }
            onClosed={() => setSelectedIds([])}
            onOpenChange={setBulkOpen}
          />
          <Button type="link" size="small" onClick={() => setSelectedIds([])}>
            {t('actions.clear')}
          </Button>
        </Flex>
      )}

      <Table
        {...tableProps}
        onRow={drag.onRow}
        rowSelection={{
          selectedRowKeys: drag.selectedRowKeys,
          onChange: (keys) => setSelectedIds(keys.map(Number)),
        }}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column<ContentMasterAssetFileOut>
          key="product_title"
          dataIndex="product_title"
          title={f('product')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_title')}
          render={(_, record) => (
            <Flex vertical>
              <ShowLink
                resource="content_catalog.products"
                id={record.product_id}
              >
                {record.product_title ?? record.product_id}
              </ShowLink>
              {record.product_isbn && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM }}
                >
                  {record.product_isbn}
                </Typography.Text>
              )}
            </Flex>
          )}
          // By id, not title: this is the drill-down a product's own page
          // links into ("show me this product's files").
          filtered={isFieldFiltered(crudFilters, 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="product_id"
              width={160}
              placeholder={f('product_id')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_type"
          title={f('product_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_type')}
          render={(value: string | null) =>
            value ? (
              <Tag color={productTypeColors[value] || 'default'}>
                {t(`content_catalog.products.product_type.${value}`, {
                  defaultValue: value,
                })}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(crudFilters, 'product_type')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_type"
              placeholder={f('product_type')}
              options={Object.values(ProductType).map((v) => ({
                label: t(`content_catalog.products.product_type.${v}`),
                value: v,
              }))}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column<ContentMasterAssetFileOut>
          dataIndex="publisher_name"
          title={f('publisher_name')}
          sorter
          sortOrder={getSortOrder(sorters, 'publisher_name')}
          render={(_, record) =>
            record.publisher_name ? (
              record.publisher_id != null ? (
                <ShowLink
                  resource="content_catalog.publishers"
                  id={record.publisher_id}
                >
                  {record.publisher_name}
                </ShowLink>
              ) : (
                record.publisher_name
              )
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="file_format"
          title={f('file_format')}
          sorter
          sortOrder={getSortOrder(sorters, 'file_format')}
          render={(value: string | null) =>
            value ? <Typography.Text code>{value}</Typography.Text> : '-'
          }
          filtered={isFieldFiltered(crudFilters, 'file_format')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="file_format"
              placeholder={f('file_format')}
              // Technical tokens; they read as themselves in every locale.
              options={Object.values(ProductFormat).map((v) => ({
                label: v,
                value: v,
              }))}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="file_size_bytes"
          title={f('file_size_bytes')}
          sorter
          sortOrder={getSortOrder(sorters, 'file_size_bytes')}
          render={(value: number | null) => formatFileSizeComma(value) ?? '-'}
        />

        {booleanColumn('is_latest')}
        {booleanColumn('is_downloaded')}

        <Table.Column
          dataIndex="transcoding_status"
          title={f('transcoding_status')}
          sorter
          sortOrder={getSortOrder(sorters, 'transcoding_status')}
          render={(value: TranscodingJobStatus | null) =>
            value ? (
              <Tag color={transcodingStatusColors[value] ?? 'default'}>
                {t(`content_catalog.transcoding_jobs.status.${value}`, {
                  defaultValue: value,
                })}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(crudFilters, 'transcoding_status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="transcoding_status"
              placeholder={f('transcoding_status')}
              options={Object.values(TranscodingJobStatus).map((v) => ({
                label: t(`content_catalog.transcoding_jobs.status.${v}`),
                value: v,
              }))}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="transcoded_at"
          title={f('transcoded_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'transcoded_at')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
          filtered={isFieldFiltered(crudFilters, 'transcoded_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="transcoded_at"
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="availability"
          title={f('availability')}
          sorter
          sortOrder={getSortOrder(sorters, 'availability')}
          render={(value: string | null) =>
            value ? (
              <Tag color={availabilityColors[value] || 'default'}>
                {t(`content_catalog.distribution_periods.filters.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(crudFilters, 'availability')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="availability"
              placeholder={f('availability')}
              options={Object.values(ContentMasterAssetFileOutAvailability).map(
                (v) => ({
                  label: t(`content_catalog.distribution_periods.filters.${v}`),
                  value: v,
                })
              )}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        {booleanColumn('publisher_has_active_contract')}

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
